import { useEffect, useMemo, useState } from "react";

import { Button, Dropdown, Form, FormField, FormGroup, Input, Modal } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import {
  BTN_ACTS,
  ExperienceDataType,
  JOB_LEVEL_OPTIONS,
  MONTH_OPTIONS,
} from "./sequence/EmployeeInfo";

type ModEmployeeExperienceProps = {
  setProp: (key: any, value: any) => void;
  birthDate?: string | null;
  experienceLength: number;
  initialData?: any;
  open: boolean;
  pendingExperienceLength: number;
  buttonLoadCheck?: string;
  languageUX: "en" | "th";
  onClose: () => void;
  onSave: (data: ExperienceDataType) => void;
};

const getInitialExperienceState = (): ExperienceDataType => ({
  id: null,
  companyAddress: "",
  companyName: "",
  endMonth: null,
  endYear: null,
  index: 0,
  jobDescription: "",
  jobLevel: "",
  jobType: "",
  position: "",
  startMonth: null,
  startYear: null,
});

const ModEmployeeExperience = (props: ModEmployeeExperienceProps) => {
  const [experienceData, setExperienceData] = useState<ExperienceDataType>(() =>
    getInitialExperienceState()
  );
  const [showRequiredField, setShowRequiredField] = useState<Record<string, string>>({});

  // Generate experience year options
  const experienceYearOptions = useMemo(() => {
    const birthYear =
      (props.birthDate ? new Date(props.birthDate).getFullYear() : new Date().getFullYear() - 60) +
      543;
    const currentYear = new Date().getFullYear() + 543;

    return Array.from({ length: currentYear - birthYear + 1 }, (_, index) => {
      const year = birthYear + index;

      return { key: year, text: `${year}`, value: year };
    }).reverse();
  }, [props.birthDate]);

  // Load initial data when modal opens
  useEffect(() => {
    if (props.open) {
      if (props.initialData) {
        // Map ข้อมูลจาก snake_case หรือ camelCase
        setExperienceData({
          id: props.initialData.id || null,
          companyAddress:
            props.initialData.companyAddress || props.initialData.company_address || "",
          companyName: props.initialData.companyName || props.initialData.company_name || "",
          endMonth: props.initialData.endMonth || props.initialData.end_month || null,
          endYear: props.initialData.endYear || props.initialData.end_year || null,
          index: props.initialData.index || 0,
          jobDescription:
            props.initialData.jobDescription || props.initialData.job_description || "",
          jobLevel: props.initialData.jobLevel || props.initialData.job_level || "",
          jobType: props.initialData.jobType || props.initialData.job_type || "",
          position: props.initialData.position || "",
          startMonth: props.initialData.startMonth || props.initialData.start_month || null,
          startYear: props.initialData.startYear || props.initialData.start_year || null,
          tempId: props.initialData.tempId,
        });
      } else {
        setExperienceData(getInitialExperienceState());
      }

      setShowRequiredField({});
    }
  }, [props.initialData, props.open]);

  const handleExperienceChange = (field: string) => (e: any, data: any) => {
    const value = data.value;

    setExperienceData((prev) => ({ ...prev, [field]: value }));
  };

  const validateExperienceData = () => {
    const errors: Record<string, string> = {};

    if (!experienceData.startMonth) {
      errors.startMonth = "กรุณาระบุเดือนที่เริ่มงาน";
    }

    if (!experienceData.startYear) {
      errors.startYear = "กรุณาระบุปีที่เริ่มงาน";
    }

    if (!experienceData.endMonth) {
      errors.endMonth = "กรุณาระบุเดือนที่สิ้นสุดงาน";
    }

    if (!experienceData.endYear) {
      errors.endYear = "กรุณาระบุปีที่สิ้นสุดงาน";
    }

    if (!experienceData.companyName.trim()) {
      errors.companyName = "กรุณาระบุชื่อบริษัท";
    }

    if (!experienceData.position.trim()) {
      errors.position = "กรุณาระบุตำแหน่งงาน";
    }

    if (
      experienceData.startYear &&
      !experienceYearOptions.some((opt) => opt.value === experienceData.startYear)
    ) {
      errors.startYear = "ปีที่เริ่มงานไม่ถูกต้อง";
    }

    if (
      experienceData.endYear &&
      !experienceYearOptions.some((opt) => opt.value === experienceData.endYear)
    ) {
      errors.endYear = "ปีที่สิ้นสุดงานไม่ถูกต้อง";
    }

    if (
      experienceData.startMonth &&
      !MONTH_OPTIONS.some((opt) => opt.value === experienceData.startMonth)
    ) {
      errors.startMonth = "เดือนที่เริ่มงานไม่ถูกต้อง";
    }

    if (
      experienceData.endMonth &&
      !MONTH_OPTIONS.some((opt) => opt.value === experienceData.endMonth)
    ) {
      errors.endMonth = "เดือนที่สิ้นสุดงานไม่ถูกต้อง";
    }

    if (
      experienceData.jobLevel &&
      !JOB_LEVEL_OPTIONS.some((opt) => opt.value === experienceData.jobLevel)
    ) {
      errors.jobLevel = "ระดับงานไม่ถูกต้อง";
    }

    if (
      experienceData.startYear &&
      experienceData.endYear &&
      (experienceData.endYear < experienceData.startYear ||
        (experienceData.endYear === experienceData.startYear &&
          experienceData.endMonth &&
          experienceData.startMonth &&
          experienceData.endMonth < experienceData.startMonth))
    ) {
      errors.endYear = "วันที่สิ้นสุดต้องมากกว่าวันที่เริ่ม";
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validateExperienceData();

    setShowRequiredField(errors);

    if (Object.keys(errors).length === 0) {
      props.onSave(experienceData);
    }
  };

  const handleClose = () => {
    setExperienceData(() => getInitialExperienceState());
    setShowRequiredField({});
    props.onClose();
  };

  return (
    <Modal
      style={{ borderRadius: 10, position: "relative" }}
      open={props.open}
      size="small"
      onClose={handleClose}
    >
      <Modal.Header
        style={{
          border: "none",
          borderRadius: 10,
          boxShadow: "none",
          color: "var(--primary-theme-color)",
          fontSize: "20px",
          paddingBottom: "0px",
          paddingTop: "24px",
        }}
      >
        ลำดับที่{" "}
        {experienceData.index === 0
          ? props.experienceLength + props.pendingExperienceLength + 1
          : experienceData.index}
      </Modal.Header>

      <Modal.Content style={{ fontWeight: "bold", paddingBottom: "3rem", textAlign: "center" }}>
        <Form>
          <FormGroup>
            <FormField
              className={showRequiredField.startMonth || showRequiredField.startYear ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Start since" : "วันที่เริ่มตั้งแต่"}
              </label>
              <div style={{ display: "flex", gap: "12px" }}>
                <Dropdown
                  style={{ fontWeight: "normal", minWidth: "150px" }}
                  placeholder="เดือน"
                  value={experienceData.startMonth ?? undefined}
                  selection
                  options={MONTH_OPTIONS}
                  onChange={handleExperienceChange("startMonth")}
                />
                <div
                  style={{
                    bottom: "-16px",
                    color: "red",
                    fontSize: "0.75rem",
                    left: "50px",
                    position: "absolute",
                  }}
                >
                  {showRequiredField.startMonth || ""}
                </div>
                <Dropdown
                  style={{ fontWeight: "normal", minWidth: "150px" }}
                  placeholder="ปี"
                  value={experienceData.startYear ?? undefined}
                  selection
                  options={experienceYearOptions}
                  onChange={handleExperienceChange("startYear")}
                />
                <div
                  style={{
                    bottom: "-16px",
                    color: "red",
                    fontSize: "0.75rem",
                    position: "absolute",
                    right: "15px",
                  }}
                >
                  {showRequiredField.startYear || ""}
                </div>
              </div>
            </FormField>
            <FormField
              className={showRequiredField.endMonth || showRequiredField.endYear ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "until" : "จนถึง"}
              </label>
              <div style={{ display: "flex", gap: "12px" }}>
                <Dropdown
                  style={{ fontWeight: "normal", minWidth: "150px" }}
                  placeholder="เดือน"
                  value={experienceData.endMonth ?? undefined}
                  selection
                  options={MONTH_OPTIONS}
                  onChange={handleExperienceChange("endMonth")}
                />
                <div
                  style={{
                    bottom: "-16px",
                    color: "red",
                    fontSize: "0.75rem",
                    left: "50px",
                    position: "absolute",
                  }}
                >
                  {showRequiredField.endMonth || ""}
                </div>
                <Dropdown
                  style={{ fontWeight: "normal", minWidth: "150px" }}
                  placeholder="ปี"
                  value={experienceData.endYear ?? undefined}
                  selection
                  options={experienceYearOptions}
                  onChange={handleExperienceChange("endYear")}
                />
                <div
                  style={{
                    bottom: "-16px",
                    color: "red",
                    fontSize: "0.75rem",
                    position: "absolute",
                    right: "15px",
                  }}
                >
                  {showRequiredField.endYear || ""}
                </div>
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField
              className={showRequiredField.companyName ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={16}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Company Name" : "ชื่อบริษัท"}
              </label>
              <Input
                name="companyName"
                placeholder="-- กรุณาระบุชื่อบริษัท --"
                value={experienceData.companyName}
                onChange={handleExperienceChange("companyName")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.companyName || ""}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField style={{ position: "relative", textAlign: "left" }} width={16}>
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Company Address" : "ที่อยู่บริษัท"}
              </label>
              <Input
                name="companyAddress"
                placeholder="-- กรุณาระบุที่อยู่บริษัท --"
                value={experienceData.companyAddress}
                onChange={handleExperienceChange("companyAddress")}
              />
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField
              className={showRequiredField.position ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Position" : "ตำแหน่งงาน"}
              </label>
              <Input
                name="position"
                placeholder="-- กรุณาระบุตำแหน่งงาน --"
                value={experienceData.position}
                onChange={handleExperienceChange("position")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.position || ""}
              </div>
            </FormField>
            <FormField style={{ textAlign: "left" }} width={8}>
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Job Type" : "ประเภทงาน"}
              </label>
              <Input
                name="jobType"
                placeholder="-- กรุณาระบุประเภทงาน --"
                value={experienceData.jobType}
                onChange={handleExperienceChange("jobType")}
              />
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField
              className={showRequiredField.jobLevel ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Job Level" : "ระดับงาน"}
              </label>
              <Dropdown
                style={{ fontWeight: "normal" }}
                placeholder="-- กรุณาระบุระดับงาน --"
                value={experienceData.jobLevel}
                selection
                options={JOB_LEVEL_OPTIONS}
                onChange={handleExperienceChange("jobLevel")}
              />
            </FormField>
            <FormField
              className={showRequiredField.jobDescription ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Job Description" : "รายละเอียดงาน"}
              </label>
              <Form.TextArea
                name="jobDescription"
                placeholder="-- กรุณาระบุรายละเอียดงาน --"
                value={experienceData.jobDescription}
                onChange={handleExperienceChange("jobDescription")}
              />
            </FormField>
          </FormGroup>
        </Form>
      </Modal.Content>
      <Modal.Actions
        style={{ border: "none", boxShadow: "none", gap: "16px", justifyContent: "center" }}
      >
        <Button onClick={handleClose}>ปิดหน้าต่าง</Button>
        <ButtonLoadCheck
          setProp={props.setProp}
          style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
          paramKey={BTN_ACTS.EXPERIENCE_SAVE}
          title="บันทึกข้อมูล"
          buttonLoadCheck={props.buttonLoadCheck}
          onClick={handleSave}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModEmployeeExperience;
